import { useEffect, useRef, useState } from "react";
import { serviceConsumer } from "../../network/ServiceConsumer";

// The server prices feis and grade registrations (security batch 6): these
// screens show its quote and send its total back with the checkout, so what a
// family sees is what they're charged. The in-browser figures remain only as a
// fallback while the quote loads.
//
// `path` is one of /stripe/quote, /stripe/grade/quote, /stripe/edit/quote,
// /stripe/edit/grade/quote; `body` is the selection (no amounts). Refetches
// half a second after the selection stops changing.
//
// A quote belongs to the exact selection it priced: it is shown (and sent)
// only while that selection is still the current one, and only the newest
// request's answer is kept, so an old or out-of-order reply can't be used.
//
// A request that fails (network, or the server refusing the selection) is
// reported as `failed` with the server's `error` message; `retry()` asks
// again for the same selection.
export function useServerQuote({ enabled, path, body }) {
  const key = enabled ? `${path}|${JSON.stringify(body)}` : "";
  const [held, setHeld] = useState({ key: "", quote: null, failed: false, error: "" });
  const [attempt, setAttempt] = useState(0);
  const generation = useRef(0);
  const started = useRef(0); // quote requests actually sent
  const currentKey = useRef(key);
  currentKey.current = key;

  useEffect(() => {
    generation.current += 1;
    const mine = generation.current;
    if (!key) return undefined;
    const timer = setTimeout(async () => {
      started.current += 1;
      let quote = null;
      let error = "";
      try {
        const response = await serviceConsumer("POST", `${process.env.REACT_APP_BASE_URL}${path}`, body);
        quote = response?.quote || null;
        if (!quote) error = "We couldn't work out the price.";
      } catch (e) {
        quote = null;
        error = e?.messages || "We couldn't work out the price.";
      }
      if (generation.current === mine) setHeld({ key, quote, failed: !quote, error });
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  const current = key && held.key === key;
  const quote = current ? held.quote : null;
  const failed = Boolean(current && held.failed);
  const error = failed ? held.error : "";
  const retry = () => setAttempt((n) => n + 1);

  // Taken when checkout is clicked: the selection, its generation, and how
  // many quote requests had been sent.
  const beginSubmission = () => ({
    key: currentKey.current,
    generation: generation.current,
    started: started.current,
  });

  // A checkout's 409 "Prices have changed" carries the server's new quote for
  // the submitted selection. Keep it only if nothing has changed since that
  // click (same selection, same generation) and no quote request was sent
  // after it (that request's answer is newer); a later change, even back to
  // the same selection, supersedes it.
  const acceptCorrection = (correction, submission) => {
    if (!correction || !submission || !submission.key) return false;
    if (
      submission.key !== currentKey.current ||
      submission.generation !== generation.current ||
      submission.started !== started.current
    ) {
      return false;
    }
    // A quote request still in flight started before the click, so it is
    // older than this correction: drop it.
    generation.current += 1;
    setHeld({ key: submission.key, quote: correction, failed: false, error: "" });
    return true;
  };

  return { quote, failed, error, retry, beginSubmission, acceptCorrection };
}

/** A quote line's amount in dollars (0 when the line is absent). */
export const quoteLine = (quote, type) => {
  const line = quote?.lines?.find((l) => l.type === type);
  return line ? line.amountCents / 100 : 0;
};

/** The server's quote attached to a 409 "Prices have changed" reply, if any. */
export const quoteFromPriceChange = (error) => {
  const data = error?.error?.response?.data;
  return error?.error?.response?.status === 409 && data?.code === "PRICE_CHANGED" ? data.quote : null;
};
